import { isEmpty, isNil } from 'lodash';
import { CSSProperties, Fragment, useCallback, useContext, useEffect, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { Button } from '@shared/components/Button';
import { Icon } from '@shared/components/Icon';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBEmptyListPlaceholder } from '@shared/components/list';
import { getTitleComponentId } from '@shared/data/entity';
import { CardFormTypeContext } from '@shared/scenes/entity/cardFormTypeContext';
import { EditField } from '@shared/scenes/entity/components/fields/EditField';
import { RelatedCardPrefills } from '@shared/scenes/entity/components/fields/RelatedEntityEditField';
import { useFormSearch } from '@shared/scenes/entity/context';
import { CardEntityError } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { FieldRecord, UpdateField } from '@shared/scenes/entity/types';
import { getFieldError } from '@shared/scenes/entity/util/getFieldError';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import {
  layoutComponentIds,
  resolveLayoutItems,
} from '@shared/scenes/workflows/resolveViewTemplateLayout';
import {
  isCombinedTagsComponent,
  isEntityBatchComponent,
  isLayoutComponent,
  isLayoutSection,
} from '@shared/scenes/workflows/utils';
import {
  AnyLayoutItem,
  isLayoutContentItem,
  isLayoutImageItem,
  isLayoutTextItem,
  isLayoutVideoItem,
} from '@shared/scenes/workflows/viewTemplateLayoutItems';
import {
  LayoutImageItem,
  LayoutTextItem,
  LayoutVideoItem,
} from '@shared/scenes/workflows/views/view-components/layoutItems';
import { grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { notNil } from '@shared/util/notNil';
import { Storage, useLocalStorage } from '@shared/util/storage';
import { EntityFormProps } from './EntityFormProps';
import { FormToolbar } from './FormToolbar';
import { matchesFieldSearch } from './matchesFieldSearch';
import styles from './EntityFormLayout.module.css';

export function EntityFormLayout({
  fields,
  updateField,
  components,
  errors,
  workflowTemplateId,
  entityId,
  isAuthenticated = true,
  layout,
}: EntityFormProps) {
  const search = useFormSearch();
  const query = search?.query ?? '';
  const isSearching = !isEmpty(query.trim());
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const formType = useContext(CardFormTypeContext);

  // Subforms and combined tags never render as a field of their own, so a form doesn't add them
  // back when the layout leaves them out.
  const layoutItems = useMemo(
    () =>
      resolveLayoutItems(components, layout, {
        shouldAppend: component =>
          !isEntityBatchComponent(component) && !isCombinedTagsComponent(component),
      }),
    [layout, components],
  );

  // Search collapses the form to a flat list of matching fields, in layout order: a match inside
  // a collapsed section would otherwise stay hidden, and text, image and video items are dropped.
  // A field with an error always stays in the list, so "check this page for errors" never points
  // at a field the search is hiding.
  const searchResults = useMemo(() => {
    if (!isSearching || isNil(fields)) {
      return [];
    }
    return layoutItems
      .flatMap(layoutComponentIds)
      .map(componentId => components.find(c => c.id === componentId))
      .filter(notNil)
      .filter(
        component =>
          matchesFieldSearch(component, fields[component.id], query) ||
          !isNil(getFieldError(errors, component.id)),
      );
  }, [isSearching, layoutItems, components, fields, query, errors]);

  // While searching an existing card, its title stays named above the results, so a search carried
  // from card to card still says which card this is. A card being created has no title to name yet.
  // The title is whichever field the workflow references cards by, not necessarily `name`.
  const searchTitle = useMemo(() => {
    if (!isSearching || isNil(fields) || formType !== 'EDIT') {
      return undefined;
    }
    const titleField = fields[getTitleComponentId(workflowTemplate)];
    if (isNil(titleField) || titleField.isEmpty) {
      return undefined;
    }
    return titleField.toDisplayString();
  }, [isSearching, fields, formType, workflowTemplate]);

  const clearSearch = useCallback(() => search?.setQuery(''), [search]);

  if (isNil(fields)) {
    return null;
  }

  return (
    <View className={styles.formContainer}>
      <FormToolbar
        search={search}
        entityId={entityId}
        workflowTemplateId={workflowTemplateId}
        components={components}
        isAuthenticated={isAuthenticated}
      />
      <View className={[styles.formContent, 'print-container']}>
        <RelatedCardPrefills
          workflowTemplateId={workflowTemplateId}
          fields={fields}
          updateField={updateField}
          entityId={entityId}
        />
        {isSearching && !isEmpty(searchResults) && (
          <>
            <View row centerVertically className={styles.searchTitle}>
              {!isNil(searchTitle) && (
                <Text size="body2" numberOfLines={1} className={styles.searchTitleText}>
                  <Trans
                    i18nKey="card.form.searchMatchingFieldsIn"
                    values={{ title: searchTitle }}
                  />
                </Text>
              )}
              {isNil(searchTitle) && (
                <Text size="body2" numberOfLines={1} className={styles.searchTitleText}>
                  {i18n('card.form.searchMatchingFields')}
                </Text>
              )}
              <Button
                size="small"
                label={i18n('card.form.showAllFields')}
                onClick={clearSearch}
                className={styles.searchTitleButton}
              />
            </View>
            <View className={styles.formSectionDivider} />
          </>
        )}
        {isSearching &&
          searchResults.map(component => (
            <EditField
              key={component.id}
              field={fields[component.id]}
              fields={fields}
              workflowTemplateId={workflowTemplateId}
              component={component}
              updateField={updateField}
              error={getFieldError(errors, component.id)}
              entityId={entityId}
              isAuthenticated={isAuthenticated}
              style={cssStyles.standaloneFormComponent}
            />
          ))}
        {isSearching && isEmpty(searchResults) && (
          <MBEmptyListPlaceholder
            title={i18n('card.form.noSearchResults.title')}
            description={i18n('card.form.noSearchResults.subtitle')}
            buttonTitle={i18n('card.form.showAllFields')}
            onButtonPress={clearSearch}
          />
        )}
        {!isSearching &&
          layoutItems.map((item, index, list) => {
            const previousItem = list[index - 1];
            const isLastItem = index === list.length - 1;
            return (
              <Fragment key={item.id}>
                <Spacer item={item} previousItem={previousItem} />
                <LayoutItem
                  item={item}
                  components={components}
                  fields={fields}
                  workflowTemplateId={workflowTemplateId}
                  updateField={updateField}
                  errors={errors}
                  entityId={entityId}
                  isAuthenticated={isAuthenticated}
                  isLastItem={isLastItem}
                />
              </Fragment>
            );
          })}
      </View>
    </View>
  );
}

/**
 * True for the items that carry their own bottom spacing and so need none added between them.
 *
 * A text, image or video item spaces itself to match a field, so `Spacer` treats it as one: no
 * gap between two of them or between one and a field, and the same divider a field earns where
 * a section follows.
 */
function spacesLikeAField(item: AnyLayoutItem) {
  return isLayoutComponent(item) || isLayoutContentItem(item);
}

function Spacer({ item, previousItem }: { item: AnyLayoutItem; previousItem?: AnyLayoutItem }) {
  if (isNil(previousItem)) {
    return null;
  }
  if (spacesLikeAField(item) && spacesLikeAField(previousItem)) {
    return null;
  } else if (isLayoutSection(item) && isLayoutSection(previousItem)) {
    return null;
  } else if (spacesLikeAField(previousItem) && isLayoutSection(item)) {
    return <View className={styles.formSectionDivider}></View>;
  } else {
    return <View className={styles.sectionComponentSpacer} />;
  }
}

interface LayoutItemProps {
  item: AnyLayoutItem;
  components: API.AnyComponent[];
  fields: FieldRecord;
  workflowTemplateId: string;
  updateField: UpdateField;
  errors: CardEntityError[];
  entityId?: string;
  isAuthenticated: boolean;
  isLastItem: boolean;
}

function LayoutItem({
  item,
  components,
  fields,
  workflowTemplateId,
  updateField,
  errors,
  entityId,
  isAuthenticated,
  isLastItem,
}: LayoutItemProps) {
  if (isLayoutSection(item)) {
    return (
      <FormSection
        section={item}
        components={components}
        fields={fields}
        workflowTemplateId={workflowTemplateId}
        updateField={updateField}
        errors={errors}
        entityId={entityId}
        isAuthenticated={isAuthenticated}
        isLastItem={isLastItem}
      />
    );
  } else if (isLayoutComponent(item)) {
    const component = components.find(c => c.id === item.componentId);
    if (isNil(component) || isNil(item.componentId)) {
      return null;
    }
    return (
      <EditField
        field={fields[item.componentId]}
        fields={fields}
        workflowTemplateId={workflowTemplateId}
        component={component}
        updateField={updateField}
        error={getFieldError(errors, item.componentId)}
        entityId={entityId}
        isAuthenticated={isAuthenticated}
        style={cssStyles.standaloneFormComponent}
      />
    );
  }

  // Text, image and video items: content to read rather than a field to fill in. Indented to the
  // same gutter as a standalone field, with their own vertical spacing, so a form of both reads
  // as one column. Empty ones never reach here — they are dropped where the list is built.
  if (!isLayoutContentItem(item)) {
    return null;
  }

  return (
    <View style={cssStyles.standaloneContentItem}>
      {isLayoutTextItem(item) && <LayoutTextItem item={item} />}
      {isLayoutImageItem(item) && <LayoutImageItem item={item} />}
      {isLayoutVideoItem(item) && <LayoutVideoItem item={item} />}
    </View>
  );
}

interface FormSectionProps {
  section: API.ViewTemplateLayoutSectionFragment;
  components: API.AnyComponent[];
  fields: FieldRecord;
  workflowTemplateId: string;
  updateField: UpdateField;
  errors: CardEntityError[];
  entityId?: string;
  isAuthenticated: boolean;
  isLastItem: boolean;
}

function FormSection({
  section,
  components,
  fields,
  workflowTemplateId,
  updateField,
  errors,
  entityId,
  isAuthenticated,
  isLastItem,
}: FormSectionProps) {
  const collapsedKey = `collapsed-form-section-${workflowTemplateId}-${section.id}`;
  const [isCollapsed, setIsCollapsed] = useLocalStorage(collapsedKey, section.isCollapsed);
  const toggleCollapsed = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, [setIsCollapsed]);

  useEffect(() => {
    if (isCollapsed === !!section.isCollapsed) {
      Storage.remove(collapsedKey);
    }
  }, [collapsedKey, isCollapsed, section.isCollapsed]);

  const sectionComponents = useMemo(() => {
    const componentIds = section.componentIds ?? [];
    return componentIds
      .map(componentId => components.find(item => item.id === componentId))
      .filter(notNil);
  }, [components, section.componentIds]);

  if (isEmpty(sectionComponents)) {
    return null;
  }

  const isTwoColumn = section.columnCount === 2;
  const sectionClassName = [styles.formSection, isTwoColumn && styles.formSectionTwoColumn]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <View className={styles.formSectionHeader}>
        <Button variant="ghost" onClick={toggleCollapsed} className={styles.formSectionTitle}>
          <View className={[styles.formSectionTitleIcon, isCollapsed && styles.isCollapsed]}>
            <Icon name="arrow-point-down-heavy" gridSize={1.75} />
          </View>
          <Text size="subtitle" numberOfLines={1}>
            {section.title}
          </Text>
        </Button>
      </View>
      {!isCollapsed && (
        <View className={styles.formSection}>
          <View className={[sectionClassName, styles.formSectionContent]}>
            {sectionComponents.map(component => (
              <EditField
                key={component.id}
                field={fields[component.id]}
                fields={fields}
                workflowTemplateId={workflowTemplateId}
                component={component}
                updateField={updateField}
                error={getFieldError(errors, component.id)}
                entityId={entityId}
                isAuthenticated={isAuthenticated}
                style={cssStyles.formComponent}
              />
            ))}
          </View>
        </View>
      )}
      {isCollapsed && !isLastItem && <View className={styles.formSectionDivider}></View>}
    </>
  );
}

const cssStyles: Record<string, CSSProperties> = {
  formComponent: {
    paddingTop: grid(0.25),
    paddingBottom: grid(1.75),
  },
  // Merged ahead of time: EditField is memoized, so the style object it
  // receives must keep a stable identity across renders.
  standaloneFormComponent: {
    paddingTop: grid(0.25),
    paddingBottom: grid(1.75),
    marginLeft: grid(2),
    marginRight: grid(2),
    border: '1px solid transparent',
  },
  // A standalone field's gutter without its vertical padding: each content item carries its own
  // bottom spacing, so a text item and an image are not separated differently.
  standaloneContentItem: {
    marginLeft: grid(2),
    marginRight: grid(2),
  },
};
